<template>
  <div
    :id="`title_achievement-card-${index + 1}`"
    ref="card"
    class="achievement-card"
    :class="cardClass"
    @mouseover="handleMouseOver"
    @mouseleave="handleMouseLeave"
    @click="navigateLink(link)"
  >
    <div class="d-flex flex-column justify-content-between" :style="{ opacity: cardOpacity }">
      <div class="d-none d-md-block achievement-icon mt-3 mb-2 mb-md-2 mb-xl-3 mb-sxl-4">
        <slot name="icon">
          <i
            v-if="bootstrap_icon"
            :class="bootstrap_icon"
            style="font-size: 2.5rem; color: black"
          ></i>
        </slot>
      </div>

      <div class="d-block d-md-none achievement-icon mr-3">
        <slot name="icon">
          <i
            v-if="bootstrap_icon"
            :class="bootstrap_icon"
            style="font-size: 2rem; color: black"
          ></i>
        </slot>
      </div>

      <div class="d-flex direction-row mt-1 mt-md-2 mt-xl-3 mt-sxl-4 align-items-center">
        <component
          :is="`h${size.tag}`"
          v-for="(size, idx) in titleSizes"
          :key="idx"
          :class="size.classes"
        >
          <slot name="default"></slot>
          <b-link class="achievement-arrow pl-3" @click.stop>
            <svg
              :class="size.arrowClass"
              :width="size.arrowWidth"
              height="16"
              viewBox="0 0 52 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M0 14H48L36 2" stroke="#111111" stroke-width="3" />
            </svg>
          </b-link>
        </component>
      </div>

      <component
        :is="`h${size.tag}`"
        v-for="(size, idx) in labelSizes"
        :key="idx"
        :class="size.classes"
      >
        <span v-html="sanitizeHtml(label)"> </span>
      </component>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import main_page_mixin from '@/mixins/main_page_mixin.js';
import { sanitizeHtml } from '@/components/sanitize.js';

export default {
  name: 'AchievementCard',
  mixins: [main_page_mixin],
  props: {
    label: {
      required: false,
      type: String,
      default: '라벨'
    },
    bootstrap_icon: {
      required: false,
      type: String,
      default: ''
    },
    link: {
      required: false,
      type: String,
      default: ''
    },
    hovered: {
      type: Boolean,
      default: false
    },
    hoveringIndex: {
      type: Number,
      default: null
    },
    index: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      isAnimationComplete: false
    };
  },
  computed: {
    cardOpacity() {
      return this.hoveringIndex !== null && this.hoveringIndex !== this.index ? 0.5 : 1;
    },
    cardClass() {
      return [
        'achievement-card',
        {
          hovering: this.hovering,
          hovered: this.hovered,
          'animation-complete': this.isAnimationComplete
        }
      ];
    },
    titleSizes() {
      return [
        {
          tag: 2,
          classes: ['achievement-title', 'fw-700', 'mr-auto mb-0', 'align-items-center'],
          arrowClass: 'ml-4 arrow-icon',
          arrowWidth: 36
        }
      ];
    },
    labelSizes() {
      return [{ tag: 6, classes: ['achievement-label', 'mb-3', 'text-keep-all'] }];
    }
  },
  mounted() {
    this.observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-line');
          this.isAnimationComplete = true;
          this.observer.unobserve(entry.target);
        }
      });
    });

    this.observer.observe(this.$refs.card);
  },
  methods: {
    sanitizeHtml,
    handleMouseOver() {
      this.$emit('hover', this.index);
    },
    handleMouseLeave() {
      this.$emit('hover', null);
    }
  }
};
</script>

<style lang="scss" scoped>
@use 'sass:math';

$linear-gradient-width: 4px;
$achievement-card-radius: 8px;
$achievement-card-radius-weight: 1px;

.achievement-card {
  position: relative;
  display: flex;
  cursor: pointer;
  overflow: hidden;

  min-height: 100px;

  .achievement-title,
  .achievement-label,
  .achievement-icon,
  .achievement-arrow svg {
    opacity: 1;
  }

  &.animation-complete::before {
    width: 100% !important;
  }

  &:hover {
    .arrow-icon {
      visibility: visible;

      transition: width 0.3s ease;
      transform: translateY(-50%);

      @media screen and (min-width: 1200px) {
        width: 36px;
      }

      @media screen and (min-width: 992px) and (max-width: 1199.98px) {
        width: 30px;
      }
      @media screen and (min-width: 576px) and (max-width: 991.98px) {
        width: 24px;
      }
      @media screen and (max-width: 575.98px) {
        width: 18px;
      }
    }

    @media screen and (min-width: 1200px) {
      .achievement-arrow {
        opacity: 1;
        transform: translateX(0px);
      }
    }

    ~ .achievement-card {
      .achievement-title,
      .achievement-label,
      .achievement-icon,
      .achievement-arrow svg {
        opacity: 0.5;
      }
    }
  }

  &.hovering:not(.hovered) {
    .achievement-title,
    .achievement-label,
    .achievement-icon,
    .achievement-arrow svg {
      opacity: 0.5;
    }
  }

  &-body {
    display: flex;
    flex: 1;
    flex-direction: column;
    background-color: #fff;
    padding: 15px;
    z-index: 3;
    margin: 4px 4px;
  }

  .achievement-arrow {
    opacity: 0;
    transition: opacity 0.3s ease, transform 0.3s ease;
    transform: translateX(-10px);

    svg {
      visibility: hidden;
      transition: width 0.3s ease;
    }
  }

  &:hover .achievement-arrow {
    opacity: 1;
    transform: translateX(0);

    svg {
      visibility: visible;
    }
  }
}

.achievement {
  &-title {
    transition: color 150ms ease-in-out;
    color: #111111;

    @media screen and (max-width: 767.98px) {
      &.en {
        font-size: 1.55rem;
      }

      &:not(.en) {
        font-size: 1.5rem;
      }
    }

    @media screen and (min-width: 768px) and (max-width: 991.98px) {
      &:not(.en) {
        font-size: 1.5rem;
      }

      &.en {
        font-size: 1.45rem;
        padding-right: 1.5rem;
      }
    }

    @media screen and (min-width: 992px) and (max-width: 1198.98px) {
      &:not(.en) {
        font-size: 1.6rem;
      }

      &.en {
        font-size: 1.6rem;
        padding-right: 10px;
      }
    }

    @media screen and (min-width: 1200px) and (max-width: 1399.98px) {
      &:not(.en) {
        font-size: 1.5rem;
      }

      &.en {
        font-size: 1.5rem;
      }
    }

    @media screen and (min-width: 1440px) and (max-width: 1679.98px) {
      &:not(.en) {
        font-size: 1.75rem;
      }

      &.en {
        font-size: 1.75rem;
        padding-right: 10px;
      }
    }

    @media screen and (min-width: 1680px) and (max-width: 1919.98px) {
      &:not(.en) {
        font-size: 2rem;
      }

      &.en {
        font-size: 2rem;
        padding-right: 10px;
      }
    }

    @media screen and (min-width: 1920px) {
      &:not(.en) {
        font-size: 2.25rem;
      }

      &.en {
        font-size: 2.25rem;
        padding-right: 10px;
      }
    }

    sub {
      bottom: 1px !important;
      font-size: 1.3rem !important;
    }
  }

  &-label {
    color: #666666;

    &.ko {
      font-weight: 400;
    }

    &.en {
      font-weight: 400;
    }

    @media screen and (max-width: 767.98px) {
      font-size: 1rem;
      word-break: keep-all;
    }

    @media (min-width: 768px) and (max-width: 991.98px) {
      font-size: 1rem;
      word-break: keep-all;
    }

    @media (min-width: 991px) and (max-width: 1198.98px) {
      font-size: 1rem;
      word-break: keep-all;
      padding-right: 2.5rem;
    }

    @media (min-width: 1200px) and (max-width: 1439.98px) {
      font-size: 1rem;
      word-break: keep-all;
      padding-right: 4.5rem;
    }

    @media screen and (min-width: 1440px) and (max-width: 1679.98px) {
      font-size: 1.05rem;
    }

    @media screen and (min-width: 1680px) and (max-width: 1919.98px) {
      font-size: 1.1rem;
      word-break: keep-all;
    }

    @media screen and (min-width: 1920px) {
      font-size: 1.15rem;
      word-break: keep-all;
    }
  }

  &-icon {
    @media screen and (max-width: 767.98px) {
      margin-bottom: auto;
    }

    @media screen and (min-width: 768px) {
      margin-bottom: auto;
    }

    img {
      @media (min-width: 768px) and (max-width: 1199.98px) {
        width: 3rem;
      }
    }
  }

  &-arrow {
    position: relative;
    background-color: transparent;
    cursor: pointer;

    svg {
      position: absolute;
      left: -50%;
      top: 50%;
      transition: width 0.3s ease-in-out;
    }

    @media (max-width: 767.98px) {
      max-width: 40px;
      max-height: 40px;

      svg {
        font-size: 1.3rem;
      }
    }

    @media (min-width: 768px) and (max-width: 1198.98px) {
      max-width: 30px;
      max-height: 30px;

      svg {
        font-size: 0.9rem;
      }
    }

    @media (min-width: 1200px) and (max-width: 1399.98px) {
      max-width: 35px;
      max-height: 35px;

      svg {
        font-size: 1rem;
      }
    }

    @media (min-width: 1400px) {
      max-width: 40px;
      max-height: 40px;

      svg {
        font-size: 1.5rem;
      }
    }
  }
}

/* 아이콘 영역이 항상 자리 차지하도록 고정 너비/높이 지정 */
.achievement-icon {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem; /* 데스크탑 기본 아이콘 크기 */
  height: 2.5rem;
}

@media (max-width: 767.98px) {
  .achievement-icon {
    width: 2rem; /* 모바일 아이콘 크기 */
    height: 2rem;
  }
}
</style>
